import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Phone, MapPin, Check } from "lucide-react";
import { Overline } from "@/components/Primitives";
import { IMG, PERSONAS, INTERESTS, OFFICES, SITE_CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company: "", country: "", email: "", phone: "", persona: "", travel_month: "", group_size: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (i) => setInterests((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, interests });
      setDone(true);
      toast.success("Enquiry received — we'll be in touch shortly.");
      setForm(empty);
      setInterests([]);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-ivory border border-sand rounded-xl px-4 py-3 text-sm text-navy placeholder:text-navy/35 outline-none focus:border-gold transition-colors";

  return (
    <>
      {/* header */}
      <section className="relative pt-36 pb-16 bg-forest text-ivory grain-dark overflow-hidden">
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
          <Overline className="text-gold">Contact / Enquiry</Overline>
          <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl md:text-6xl tracking-tight max-w-3xl">Start Planning With Us</h1>
          <p className="mt-5 max-w-2xl text-lg font-light text-ivory/75">
            Tell us a little about your dream trip — how you love to travel and what you hope to
            feel — and our local team will craft the right journey for you.
          </p>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28" data-testid="contact-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          {/* form */}
          <div className="lg:col-span-8 min-w-0">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border border-sand bg-coconut p-12 text-center" data-testid="contact-success"
              >
                <div className="mx-auto w-16 h-16 rounded-full bg-teal/10 flex items-center justify-center text-teal"><Check size={28} /></div>
                <h2 className="mt-6 font-serif text-3xl text-forest">Thank you</h2>
                <p className="mt-3 text-navy/65 font-light max-w-md mx-auto">
                  Your enquiry has reached our team. We typically respond within one business day.
                </p>
                <button onClick={() => setDone(false)} className="mt-8 rounded-full border border-forest text-forest px-7 py-3 text-sm font-semibold hover:bg-forest hover:text-ivory transition-colors" data-testid="contact-new-enquiry">
                  Send another enquiry
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="rounded-lg border border-sand bg-coconut p-4 sm:p-6 md:p-10 space-y-6" data-testid="enquiry-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Name *"><input data-testid="field-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" /></Field>
                  <Field label="Company name"><input data-testid="field-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company (optional)" /></Field>
                  <Field label="Country"><input data-testid="field-country" className={inputCls} value={form.country} onChange={set("country")} placeholder="Country" /></Field>
                  <Field label="Email *"><input data-testid="field-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
                  <Field label="Phone / WhatsApp"><input data-testid="field-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+00 000 000 000" /></Field>
                  <Field label="I am a">
                    <select data-testid="field-persona" className={inputCls} value={form.persona} onChange={set("persona")}>
                      <option value="">Select one</option>
                      {PERSONAS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label="Interested in">
                  <div className="flex flex-wrap gap-2.5" data-testid="field-interests">
                    {INTERESTS.map((i) => {
                      const on = interests.includes(i);
                      return (
                        <button
                          type="button" key={i} onClick={() => toggle(i)}
                          data-testid={`interest-${i.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`min-h-11 rounded-full px-4 py-2 text-sm border transition-colors ${on ? "bg-forest text-ivory border-forest" : "bg-ivory text-navy/70 border-sand hover:border-gold"}`}
                        >
                          {i}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Approximate travel month"><input data-testid="field-month" className={inputCls} value={form.travel_month} onChange={set("travel_month")} placeholder="e.g. November 2026" /></Field>
                  <Field label="Group size"><input data-testid="field-group" className={inputCls} value={form.group_size} onChange={set("group_size")} placeholder="e.g. 2 travellers" /></Field>
                </div>

                <Field label="Message">
                  <textarea data-testid="field-message" rows={4} className={inputCls + " resize-none"} value={form.message} onChange={set("message")} placeholder="Tell us about your travellers, pace and interests..." />
                </Field>

                <button
                  type="submit" disabled={loading} data-testid="enquiry-submit"
                  className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full bg-gold text-forest px-6 sm:px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-1 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Start Planning With Us"} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>

          {/* side */}
          <aside className="lg:col-span-4 min-w-0 space-y-6">
            <div className="rounded-2xl overflow-hidden h-56"><img src={IMG.backwatersSunset} alt="India backwaters" className="w-full h-full object-cover" /></div>
            <div className="rounded-2xl border border-sand bg-coconut p-7 space-y-5">
              <h3 className="font-serif text-2xl text-forest">Talk to our team</h3>
              <Info icon={Mail} label="Email" value={`${SITE_CONTACT.email} / ${SITE_CONTACT.inboundEmail}`} />
              <Info icon={Phone} label="Phone / WhatsApp" value={SITE_CONTACT.phone} />
              <Info icon={MapPin} label="Based in" value={SITE_CONTACT.address} />
            </div>
            <div className="rounded-2xl bg-forest text-ivory p-7">
              <p className="overline text-gold">Good to know</p>
              <p className="mt-3 font-light text-ivory/85">Every journey is handcrafted — there are no fixed packages, just experiences designed around you.</p>
            </div>
          </aside>
        </div>
      </section>
      <section className="bg-sand/35 py-16">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Overline className="text-gold">Our offices</Overline>
          <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-4 gap-5">
            {OFFICES.map((office) => (
              <div key={office.city} className="border border-sand bg-ivory rounded-lg p-6">
                <h2 className="font-serif text-2xl text-forest">{office.city}</h2>
                <p className="mt-3 text-sm text-navy/60 leading-relaxed">{office.address}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold tracking-wide text-navy/60 uppercase">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-teal/10 flex items-center justify-center text-teal shrink-0"><Icon size={17} /></div>
    <div className="min-w-0">
      <p className="text-xs text-navy/45">{label}</p>
      <p className="text-navy/80 text-sm font-medium break-words">{value}</p>
    </div>
  </div>
);
